import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useMemo, useState } from 'react'
import type { ComboboxItem } from '@/components'
import {
  Combobox,
  Field,
  FieldLabel,
  Input,
  PromptModal,
  UserCombobox,
} from '@/components'
import { EMPLOYER_TAX_ZONE_CODES } from '@/modules/companies/consts'
import type { TaxUnitForm } from '../types'

interface TaxUnitPromptProps {
  initialData?: TaxUnitForm

  onSubmit: PromptResolve<TaxUnitForm | null>
}

export const TaxUnitPrompt: React.FC<TaxUnitPromptProps> = ({
  initialData,
  onSubmit,
}) => {
  const translations = useTranslate({
    addTaxUnit: 'settings.employer-tax.add-tax-unit',
    editTaxUnit: 'settings.employer-tax.edit-tax-unit',
    zone: 'settings.employer-tax.zone',
    zoneName: ['settings.employer-tax.zone-name', { zone: '' }],
    name: 'common.name',
    users: 'common.users',
    create: 'common.create-alt',
    update: 'common.update',
  })

  const editing = typeof initialData !== 'undefined'
  const [form, setForm] = useState<TaxUnitForm>(
    initialData ?? { name: '', zoneCode: '', users: [] }
  )

  const zoneItems = useMemo<ComboboxItem<string>[]>(
    () =>
      EMPLOYER_TAX_ZONE_CODES.map(zone => ({
        value: zone,
        label: translations.zoneName({ zone }),
      })),
    [translations]
  )

  const valid = form.name.trim() !== '' && form.zoneCode !== ''

  return (
    <PromptModal
      title={editing ? translations.editTaxUnit : translations.addTaxUnit}
      submitText={editing ? translations.update : translations.create}
      submitDisabled={!valid}
      cancelOnClickOutside
      onCancel={() => onSubmit(null)}
      onSubmit={() => onSubmit({ ...form, name: form.name.trim() })}
      className="w-[480px]"
    >
      <div className="flex flex-col gap-4">
        <Field>
          <FieldLabel>{translations.name}</FieldLabel>
          <Input
            value={form.name}
            autoFocus
            onChange={name => setForm(prev => ({ ...prev, name }))}
          />
        </Field>

        <Field>
          <FieldLabel>{translations.zone}</FieldLabel>
          <Combobox
            mode="single"
            value={form.zoneCode}
            items={zoneItems}
            noSearch
            onChange={zoneCode => setForm(prev => ({ ...prev, zoneCode }))}
          />
        </Field>

        <Field>
          <FieldLabel>{translations.users}</FieldLabel>
          <UserCombobox
            mode="multiple"
            value={form.users}
            onChange={users => setForm(prev => ({ ...prev, users }))}
          />
        </Field>
      </div>
    </PromptModal>
  )
}
